import React, { useState, useRef, useCallback } from 'react';
import MobileBottomSheet from './MobileBottomSheet';

const BG_COLORS = [
  '#ffffff', '#f3f4f6', '#1f2937', '#000000',
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6',
];

export default function MobileMoreSheet({ isOpen, onClose, canvasManager, layers, activeObject }) {
  const [section, setSection] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [clipartQuery, setClipartQuery] = useState('');
  const [clipartResults, setClipartResults] = useState([]);
  const [clipartLoading, setClipartLoading] = useState(false);
  const bgFileRef = useRef(null);

  const [activeBg, setActiveBg] = useState('#ffffff');
  const handleBgColor = (color) => {
    if (!canvasManager) return;
    setActiveBg(color);
    canvasManager.setBackgroundColor(color);
  };

  const handleBgImage = (e) => {
    const file = e.target.files?.[0];
    if (!file || !canvasManager) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      canvasManager.setBackgroundImage(ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleBgRemove = () => {
    if (!canvasManager) return;
    canvasManager.setBackgroundColor('#ffffff');
    if (canvasManager.canvas) {
      canvasManager.canvas.setBackgroundImage(null, () => canvasManager.canvas.renderAll());
    }
  };

  const handleAddQR = async () => {
    if (!canvasManager || !qrUrl.trim()) return;
    await canvasManager.addQRCode(qrUrl.trim());
    setQrUrl('');
    onClose();
  };

  const handleSearchCliparts = useCallback(async () => {
    if (!clipartQuery.trim()) return;
    setClipartLoading(true);
    try {
      const q = encodeURIComponent(clipartQuery.trim());
      const url = `https://pixabay.com/api/?key=33745979-6e793453ab99977fccede77c0&q=${q}&image_type=illustration&per_page=21&lang=de`;
      const res = await fetch(url);
      const data = await res.json();
      setClipartResults(data.hits || []);
    } catch (err) {
      console.error('Clipart search error:', err);
    } finally {
      setClipartLoading(false);
    }
  }, [clipartQuery]);

  const handleSelectClipart = (img) => {
    if (!canvasManager) return;
    const canvasW = canvasManager.vWidth || 3780;
    canvasManager.addImage(img.largeImageURL || img.webformatURL, {
      maxWidth: Math.round(canvasW * 0.3),
      crossOrigin: 'anonymous',
    });
    onClose();
  };

  const handleLayerSelect = (obj) => {
    if (!canvasManager?.canvas) return;
    canvasManager.canvas.setActiveObject(obj);
    canvasManager.canvas.renderAll();
    onClose();
  };

  const sectionBtn = (id, label, icon) => (
    <button
      onClick={() => setSection(section === id ? null : id)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '14px', border: '1px solid var(--selenny-border)', borderRadius: '12px',
        background: section === id ? 'var(--selenny-primary)' : 'var(--selenny-surface)',
        color: section === id ? '#fff' : 'var(--selenny-text)',
        cursor: 'pointer', width: '100%', textAlign: 'left', fontSize: '14px', fontWeight: 500,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <MobileBottomSheet isOpen={isOpen} onClose={() => { setSection(null); onClose(); }} title="Mehr" height="50vh">
      {!section && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {sectionBtn('bg', 'Hintergrund', <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 16l5-5 4 4 4-6 5 7"/></svg>)}
          {sectionBtn('qr', 'QR-Code', <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/></svg>)}
          {sectionBtn('cliparts', 'Cliparts', <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>)}
          {sectionBtn('layers', 'Ebenen', <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>)}
        </div>
      )}

      {/* Hintergrund */}
      {section === 'bg' && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, opacity: 0.5, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hintergrundfarbe</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
            {BG_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => handleBgColor(c)}
                style={{
                  width: 44, height: 44, borderRadius: '12px',
                  background: c,
                  border: activeBg === c ? '3px solid #3b82f6' : `2px solid ${c === '#ffffff' ? '#d1d5db' : 'transparent'}`,
                  cursor: 'pointer', padding: 0,
                  boxShadow: activeBg === c ? '0 0 0 2px rgba(59,130,246,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: '12px', fontWeight: 600, opacity: 0.5, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hintergrundbild</div>
          <input ref={bgFileRef} type="file" accept="image/*" onChange={handleBgImage} style={{ display: 'none' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => bgFileRef.current?.click()}
              style={{
                flex: 1, padding: '12px', background: 'var(--selenny-primary)', color: '#fff',
                border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Bild hochladen
            </button>
            <button
              onClick={handleBgRemove}
              style={{
                padding: '12px 16px', background: '#fef2f2', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: '10px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Entfernen
            </button>
          </div>
        </div>
      )}

      {/* QR-Code */}
      {section === 'qr' && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, opacity: 0.5, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>QR-Code erstellen</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="url"
              value={qrUrl}
              onChange={(e) => setQrUrl(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddQR(); }}
              style={{
                flex: 1, padding: '12px', border: '1px solid var(--selenny-border)',
                borderRadius: '10px', fontSize: '16px', background: 'var(--selenny-surface)',
                color: 'var(--selenny-text)', outline: 'none',
              }}
            />
            <button
              onClick={handleAddQR}
              disabled={!qrUrl.trim()}
              style={{
                padding: '12px 20px', background: 'var(--selenny-primary)', color: '#fff',
                border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 500,
                cursor: 'pointer', opacity: qrUrl.trim() ? 1 : 0.4,
              }}
            >
              Erstellen
            </button>
          </div>
        </div>
      )}

      {/* Cliparts */}
      {section === 'cliparts' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              value={clipartQuery}
              onChange={(e) => setClipartQuery(e.target.value)}
              placeholder="Clipart suchen..."
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchCliparts(); }}
              style={{
                flex: 1, padding: '10px 12px', border: '1px solid var(--selenny-border)',
                borderRadius: '8px', fontSize: '16px', background: 'var(--selenny-surface)',
                color: 'var(--selenny-text)', outline: 'none', minWidth: 0,
              }}
            />
            <button
              onClick={handleSearchCliparts}
              style={{
                padding: '10px 16px', background: 'var(--selenny-primary)', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
              }}
            >
              Suchen
            </button>
          </div>
          {clipartLoading && <div style={{ textAlign: 'center', padding: '16px', opacity: 0.5, color: 'var(--selenny-text)' }}>Suche...</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {clipartResults.map((img) => (
              <button
                key={img.id}
                onClick={() => handleSelectClipart(img)}
                style={{
                  padding: 6, border: '1px solid var(--selenny-border)', borderRadius: '8px',
                  overflow: 'hidden', cursor: 'pointer', background: 'var(--selenny-bg)',
                  aspectRatio: '1', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <img src={img.previewURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ebenen */}
      {section === 'layers' && (
        <div>
          {layers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', opacity: 0.5, fontSize: '13px', color: 'var(--selenny-text)' }}>
              Keine Objekte vorhanden
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[...layers].reverse().map((obj, i) => (
                <button
                  key={i}
                  onClick={() => handleLayerSelect(obj)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', border: obj === activeObject ? '2px solid var(--selenny-primary)' : '1px solid var(--selenny-border)',
                    borderRadius: '8px', background: obj === activeObject ? 'rgba(59,130,246,0.05)' : 'var(--selenny-surface)',
                    cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                    color: 'var(--selenny-text)', WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <span style={{ fontSize: '11px', opacity: 0.4, width: '20px' }}>{layers.length - i}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {obj.type === 'i-text' || obj.type === 'textbox' ? `"${(obj.text || '').substring(0, 20)}"` : obj.type === 'image' ? 'Bild' : obj.name || obj.type}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </MobileBottomSheet>
  );
}
